// Componente Button reutilizable para la aplicación Amarsi Control Panel
// Proporciona diferentes estilos y estados para botones interactivos

import React from 'react';
import { twMerge } from 'tailwind-merge';

/**
 * Componente Button
 * 
 * Botón reutilizable con múltiples variantes y estados
 * Soporta loading, disabled y diferentes tipos de acción
 * 
 * @param {Object} props - Propiedades del componente
 * @param {string} props.variant - Variante del botón (primary, secondary, danger, success, warning, outline, ghost-primary, ghost-secondary, ghost-danger, ghost-success, ghost-warning)
 * @param {string} props.size - Tamaño del botón (sm, md, lg)
 * @param {boolean} props.loading - Estado de carga
 * @param {boolean} props.disabled - Estado deshabilitado
 * @param {string} props.className - Clases CSS adicionales
 * @param {React.ReactNode} props.children - Contenido del botón
 * @param {Function} props.onClick - Función al hacer clic
 * @param {string} props.type - Tipo de botón (button, submit, reset)
 */
const Button = ({
  variant = 'primary',
  size = 'sm',
  loading = false,
  disabled = false,
  loadingText = '',
  className = '',
  children,
  onClick,
  type = 'button',
  ...props
}) => {
  // Define las clases base según la variante
  const variantClasses = {
    primary: 'bg-primary-500 hover:bg-primary-700 text-white border-primary-500 dark:bg-primary-600 dark:hover:bg-primary-700 dark:border-primary-600',
    secondary: 'bg-neutral-600 hover:bg-neutral-700 text-white border-neutral-600 dark:bg-neutral-700 dark:hover:bg-neutral-600 dark:border-neutral-500',
    danger: 'bg-red-600 hover:bg-red-700 text-white border-red-600 dark:bg-red-500 dark:hover:bg-red-600 dark:border-red-500',
    success: 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600 dark:bg-emerald-500 dark:hover:bg-emerald-600 dark:border-emerald-500',
    warning: 'bg-yellow-600 hover:bg-yellow-700 text-white border-yellow-600 dark:bg-yellow-500 dark:hover:bg-yellow-600 dark:border-yellow-500',
    outline: 'bg-transparent hover:bg-neutral-50 text-zinc-700 border-zinc-300 dark:hover:bg-neutral-900 dark:text-zinc-300 dark:border-zinc-600',
    // Variantes ghost con transparencias - colores de texto ajustados a color-500
    'ghost-primary': 'bg-primary-500/20 hover:bg-primary-800 hover:bg-opacity-10 text-primary-500 border-transparent dark:text-primary-400 dark:hover:bg-primary-600 dark:hover:bg-opacity-10',
    'ghost-secondary': 'bg-neutral-600/20 hover:bg-neutral-600 hover:bg-opacity-10 text-neutral-500 border-transparent dark:text-neutral-400 dark:hover:bg-neutral-500 dark:hover:bg-opacity-10',
    'ghost-danger': 'bg-red-600/20 hover:bg-red-600 hover:bg-opacity-10 text-red-500 border-transparent dark:text-red-400 dark:hover:bg-red-500 dark:hover:bg-opacity-10',
    'ghost-success': 'bg-emerald-600/20 hover:bg-emerald-600 hover:bg-opacity-10 text-emerald-500 border-transparent dark:text-emerald-400 dark:hover:bg-emerald-500 dark:hover:bg-opacity-10',
    'ghost-warning': 'bg-yellow-600/20 hover:bg-yellow-600 hover:bg-opacity-10 text-yellow-500 border-transparent dark:text-yellow-400 dark:hover:bg-yellow-500 dark:hover:bg-opacity-10'

  };

  // Define las clases según el tamaño
  const sizeClasses = {
    sm: 'px-2 py-2 text-sm text-xs',
    md: 'px-4 py-2 text-base text-md',
    lg: 'px-12 py-3 text-lg text-lg'
  };

  // Combina todas las clases
  const buttonClasses = twMerge(
    'inline-flex gap-2 items-center justify-center border font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-neutral-800',
    variantClasses[variant],
    sizeClasses[size],
    loading || disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
    className
  );

  // Define el color del focus ring según la variante
  const focusRingColor = {
    primary: 'focus:ring-primary-900',
    secondary: 'focus:ring-neutral-500',
    danger: 'focus:ring-red-500',
    success: 'focus:ring-emerald-500',
    warning: 'focus:ring-yellow-500',
    outline: 'focus:ring-neutral-500',
    // Focus ring para variantes ghost - mantienen colores de las variantes base
    'ghost-primary': 'focus:ring-primary-900',
    'ghost-secondary': 'focus:ring-neutral-500',
    'ghost-danger': 'focus:ring-red-500',
    'ghost-success': 'focus:ring-emerald-500',
    'ghost-warning': 'focus:ring-yellow-500'
  };

  // Maneja el clic del botón
  const handleClick = (e) => {
    if (loading || disabled) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  return (
    <button
      type={type}
      className={twMerge(buttonClasses, focusRingColor[variant])}
      onClick={handleClick}
      disabled={loading || disabled}
      {...props}
    >
      {/* Spinner de carga */}
      {loading && (
        <svg
          className="animate-spin h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      )}
      
      {/* Contenido del botón */}
      {loading && loadingText ? loadingText : children}
    </button>
  );
};

export default Button;
